import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/rootState';
import { ResourceStrings } from '../../../ResourceStrings';
import styled from 'styled-components/macro';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { useHistory, useParams } from 'react-router';
import RichPostDetail from './RichPostDetail';
import { useTicketWithDetails } from '../../../hooks/posts';
import { stringFromErrorOrAny } from '../../../utils/tools';
import { TicketContext } from './TicketContext';
import { useQueryParam } from '../../../hooks/routing';
import { referrerNative, UrlParam } from '../../../constants/urlParams';
import { platformActions } from '../../../communication/Communication';
import { MessageBar, MessageBarType } from '@fluentui/react/lib/MessageBar';
import { History } from "history";
import Panel from '../../shared/Panel';
import { getWindowTitle } from '../../../config/serverConfig';
import { SignInState } from '../../../app';

const timeWindowInHoursToShowDifferentAccountMessage = 0.5;

interface PostDetailViewUrlParams {
    title: string;
    postId: string;
}

export interface PostDetailsViewProps {
}

export default function PostDetailsView(props: PostDetailsViewProps): JSX.Element {
    const { postId: postIdParam } = useParams<PostDetailViewUrlParams>();
    const postId = Number.parseInt(postIdParam);
    const signinState = useSelector((state: RootState) => state.appState.signInState);
    const ticketDetails = useTicketWithDetails(postId);
    const state = useSelector((state: RootState) => state.appState);
    const userId = useSelector((state: RootState) => state.appState.userVsid);
    const isInReadOnlyMode = useSelector((state: RootState) => state.appState.isInReadOnlyMode);
    const history = useHistory();

    const title = useMemo(() => ticketDetails?.ticket?.data?.title, [ticketDetails]);

    // logic to inform user that they have opened this page from another window
    // where they were logged in via VS using a different account.
    const referrer = useQueryParam(UrlParam.Referrer);
    const referrerTimeStampStr = useQueryParam(UrlParam.ReferrerTimestamp);
    const referrerUserId = useQueryParam(UrlParam.ReferrerUserId);
    const isNativeProcessAvailable = !!platformActions.getNativeActions();
    const wasReferredByNative = referrer === referrerNative;
    const referrerTimeStamp: number = Number.parseInt(referrerTimeStampStr);
    const deltaHours = (Date.now() - referrerTimeStamp) / (60 * 60 * 1000);
    const hasExpired = isNaN(deltaHours) || (deltaHours > timeWindowInHoursToShowDifferentAccountMessage); // Don't show message if too much time has passed since the referring page was opened (to avoid showing message if user bookmarks/shares link)

    useEffect(() => {
        if (title) {
            document.title = `${title} - ${getWindowTitle()}`;
        } else {
            document.title = getWindowTitle();
        }
        return () => {
            document.title = getWindowTitle();
        }
    }, [title]);

    const clearReferrerParams = (history: History): void => {
        const urlParams = new URLSearchParams(history.location.search);
        urlParams.delete(UrlParam.Referrer);
        urlParams.delete(UrlParam.ReferrerTimestamp);
        urlParams.delete(UrlParam.ReferrerUserId);
        history.replace(`${history.location.pathname}?${urlParams.toString()}`);
    };

    useEffect(() => {
        return () => clearReferrerParams(history);
    }, [history]);

    const authenticating = signinState === SignInState.Checking || signinState === SignInState.Executing;

    return <Wrapper data-cy="rap-post-details-view">
        <PageContent>
            <>
                {!isNativeProcessAvailable && wasReferredByNative && !hasExpired && !authenticating && userId !== referrerUserId && (
                    <div css="padding: 2rem var(--page-margin-horizontal)">
                        <MessageBar
                            messageBarType={MessageBarType.warning}
                            onDismiss={() => clearReferrerParams(history)}
                            dismissButtonAriaLabel={ResourceStrings.close()}
                        >
                            You are viewing this page using a different account than the one used for opening it. As a result some parts of this page could be different from the referring page.
                        </MessageBar>
                    </div>
                )}

                {(authenticating || ticketDetails.ticket?.isFetching) && <Fetching>
                    <Spinner size={SpinnerSize.large} />
                    <div>Fetching feedback item ...</div>
                </Fetching>}

                {!authenticating && !ticketDetails.ticket?.isFetching && ticketDetails.ticket?.hasErrors && <div css="padding: 2rem var(--page-margin-horizontal)">
                    <div>{"We were unable to get this feedback item. It could be because you don't have access to it or it doesn't exist"}</div>
                    <br />
                    {state.signInState !== SignInState.Success && <><div>Please try again after signing in.</div><br /></>}
                    <div>Error details: {stringFromErrorOrAny(ticketDetails.ticket?.errors)}</div>
                </div>}
                {!authenticating && !ticketDetails.ticket?.isFetching && !ticketDetails.ticket?.hasErrors && <>
                    {isInReadOnlyMode && <Panel css="background-color: #f3edcc; margin: .5rem var(--page-margin-horizontal); padding: 1rem 2rem"><b>⚠</b> Some features on this page are not available because the website is currently in read-only mode.</Panel>}

                    <TicketContext.Provider value={{ ticketDetails: ticketDetails }}>
                        <RichPostDetail
                            signInState={state.signInState}
                            userRoles={state.userRoles}
                        />
                    </TicketContext.Provider>
                </>}
            </>
        </PageContent>
    </Wrapper >
}

const Wrapper = styled.div`
    background-color: var(--color-background);
`;

// @TODO: Consolidate some common styling in PageContent on different pages
const PageContent = styled.main`
    min-height: 60vh;
    margin-left: auto;
    margin-right: auto;
    padding: 1.5rem 0;

    >* + * {
            margin-top: 2rem;
    }

    .ms-Dropdown-container {
        display: inline-block;
        min-width: 15rem;
        margin-top: .5rem;
    }

    .ms-Dropdown-title {
            border-color: var(--color-surface-dark1);
    }

    .title-instruction {
        font-size: 1.8rem;
        line-height: 2.4rem;
        font-weight: 350;

        .btn-link {
            padding: 0;
            height: auto;
        }
    }

    .selected-post-header.collapsed {
        background-color: var(--color-secondary);
    }

    .selected-post-header {
        font-size: 2.4rem;
    }

    .toolbar {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .ms-MessageBar {
        padding: .2rem 0;

        span {
            font-size: 1.6rem;
        }

        .ms-MessageBar-icon {
            font-size: 1.8rem;
        }
    }

    .accordion-content {
        border: none;
    }
`;

const Fetching = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 10rem;

    > * + * {
        margin-top: 3rem;
    }
`;